import { useNavigate } from "react-router-dom";
import { ThemeToggle } from "@/components/theme-toggle";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { UserProfile } from "@/types/profile";
import { LogOut, User } from "lucide-react";
import { SlikeLogo } from "./SlikeLogo";
import { useAppSelector, useAppDispatch } from "@/store/hooks";
import { toast } from "sonner";
import { handleLogout as performLogout } from "@/utils/logout";
import { useState, useRef } from "react";

interface AppHeaderProps {
    userName?: string;
    profile?: UserProfile | null;
    profileImage?: string;
}

export function AppHeader({ userName, profile, profileImage }: AppHeaderProps) {
    const navigate = useNavigate();
    const { sessions } = useAppSelector((state) => state.auth);
    const dispatch = useAppDispatch();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const handleLogout = async () => {
        const userId = profile?.id;
        await performLogout({ userId, sessions, dispatch, navigate });
        toast.success("Logged out successfully");
    };

    const handleMouseEnter = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        setIsDropdownOpen(true);
    };

    const handleMouseLeave = () => {
        timeoutRef.current = setTimeout(() => {
            setIsDropdownOpen(false);
        }, 150);
    };

    return (
        <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/95 border-b border-border/50 shadow-sm">
            <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">

                    {/* Left: Logo & Brand */}
                    <button
                        onClick={() => navigate("/home")}
                        className="flex items-center gap-3 group"
                    >
                        <SlikeLogo />
                    </button>

                    {/* Right: Theme Toggle + User Profile */}
                    <div className="flex items-center gap-3">
                        {/* Theme Toggle */}
                        <ThemeToggle />

                        {/* User Profile Dropdown */}
                        <div
                            onMouseEnter={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                            className="relative"
                        >
                            <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
                                <DropdownMenuTrigger asChild>
                                    <div
                                        onClick={() => navigate("/me")}
                                        className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer border border-transparent hover:border-border/50 outline-none"
                                    >
                                        <div className="hidden sm:flex flex-col items-end">
                                            <span className="text-sm font-medium truncate max-w-[120px]">
                                                {userName || "User"}
                                            </span>
                                        </div>

                                        <div className="relative">
                                            <Avatar className="w-9 h-9 border-2 border-primary/20">
                                                <AvatarImage src={profileImage} className="object-cover" />
                                                <AvatarFallback className="bg-gradient-to-br from-primary/90 to-primary/70 text-primary-foreground text-xs font-bold">
                                                    {userName?.charAt(0)?.toUpperCase() || "U"}
                                                </AvatarFallback>
                                            </Avatar>
                                            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 border-2 border-background rounded-full" />
                                        </div>
                                    </div>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                    align="end"
                                    className="w-56"
                                    sideOffset={8}
                                    onMouseEnter={handleMouseEnter}
                                    onMouseLeave={handleMouseLeave}
                                >
                                    <DropdownMenuLabel>My Account</DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => navigate("/me")} className="cursor-pointer">
                                        <User className="mr-2 h-4 w-4" />
                                        <span>Profile</span>
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                        onClick={handleLogout}
                                        className="cursor-pointer text-red-600 dark:text-red-400 focus:text-red-600 dark:focus:text-red-300 data-[highlighted]:bg-red-50 data-[highlighted]:text-red-700 dark:data-[highlighted]:bg-red-950/40 dark:data-[highlighted]:text-red-200"
                                    >
                                        <LogOut className="mr-2 h-4 w-4" />
                                        <span>Log out</span>
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    </div>
                </div>
            </div>
        </header>
    );
}
